import { useState, useEffect } from "react";
import AuthLayout from "../../containers/auth";
import toast from "react-hot-toast";
import api from "../../utils/axiosConfig";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { Link } from "react-router-dom";
import google from "../../assets/google.svg";
import { FaApple } from "react-icons/fa";
import group28 from "../../assets/Group 28.svg";
import msg from "../../assets/msg.svg";
import ph from "../../assets/ph.svg";
import eyeOpen from "../../assets/eye-open.svg";
import eyeSlash from "../../assets/eye-slash.svg";
import lockIcon from "../../assets/lock.svg";
import codeIcon from "../../assets/code.svg";
import fball from "../../assets/fball.svg";

const MobileSignup = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [refCode, setRefCode] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (token) {
      const decodedToken = jwtDecode(token);

      if (decodedToken.exp * 1000 < Date.now()) {
        localStorage.removeItem("token");
      } else {
        navigate("/account/home");
      }
    }

    return () => {};
  }, [navigate])

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get("referral");
    if (code) {
      setRefCode(code);
    }
  }, [])

  const handleSendCode = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error("Email is required");
      return;
    }

    setIsSendingCode(true);
    try {
      const response = await api.post("/auth/send-code", { email });

      if (response.status === 200) {
        setCodeSent(true);
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response.data.message);
    } finally {
      setIsSendingCode(false);
    }
  }

  const handleResendCode = async (e) => {
    e.preventDefault();

    setIsSendingCode(true);
    try {
      const response = await api.post("/auth/resend-code", { email });

      if (response.status === 200) {
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response.data.message);
    } finally {
      setIsSendingCode(false);
    }
  }

  const handleSignup = async (e) => {
    e.preventDefault();

    if (!firstName || !lastName || !email || !phoneNumber || !password || !confirmPassword || !code) {
      toast.error("Please fill all required fields");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setIsSigningUp(true);

    try {
      const response = await api.post("/auth/register", {
        firstName,
        lastName,
        email,
        phone: phoneNumber,
        password,
        code,
        refCode
      });

      if (response.status === 201) {
        localStorage.setItem("token", response.data.token);
        window.location.href = "/account/home?ismobile=true";
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response.data.message);
    } finally {
      setIsSigningUp(false);
    }
  }

  const handleGoogleSignup = (e) => {
    e.preventDefault();
    window.location.href = `https://tweezers-tweezers-api.sliplane.app/api/auth/google`;
  }

  const handleAppleSignup = (e) => {
    e.preventDefault();
    window.location.href = `https://tweezers-tweezers-api.sliplane.app/api/auth/apple`;
  }

  return (
    <AuthLayout imageSrc={fball} filter="grayscale">
      <h2 className="mb-2 text-2xl md:text-2xl font-bold text-secondary text-center">
        Precision. Predictions. Profits.
      </h2>
      <p className="mb-6 text-sm text-gray-600 text-center">
        Your Winning Journey Begins Now – Sign Up!
      </p>

      <button 
        className="w-full mt-2 inline-flex justify-center gap-4 rounded-2xl bg-white text-gray-600 border border-accent py-3 hover:bg-opacity-90"
        onClick={handleGoogleSignup}
      >
        <span>
          <img src={google} alt="google" />
        </span>
        <span>Continue with Google</span>
      </button>

      <button 
        className="w-full mt-2 inline-flex justify-center gap-4 rounded-2xl bg-black text-white border border-accent py-3 hover:bg-opacity-90"
        onClick={handleAppleSignup}
      >
        <span>
          <FaApple className="w-6 h-6" />
        </span>
        <span>Continue with Apple</span>
      </button>

      <div className="w-full flex items-center gap-2 relative top-6">
        <div className="flex-1 border-t border-accent"></div>
        <p className="text-accent font-semibold text-base mx-4">OR</p>
        <div className="flex-1 border-t border-accent"></div>
      </div>

      <form className="w-full max-w-sm mt-12">
        <div className="mb-4 flex items-center rounded-2xl border border-gray-300 px-4 py-2">
          <img src={group28} alt="user-icon" />
          <input type="text" placeholder="First Name" className="ml-2 flex-1 border-none bg-transparent text-accent outline-none py-2 px-4" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </div>
        <div className="mb-4 flex items-center rounded-2xl border border-gray-300 px-4 py-2">
          <img src={group28} alt="user-icon" />
          <input type="text" placeholder="Last Name" className="ml-2 flex-1 border-none bg-transparent text-accent outline-none py-2 px-4" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
        <div className="mb-4 flex items-center rounded-2xl border border-gray-300 px-4 py-2">
          <img src={msg} alt="msg-icon" />
          <input type="text" placeholder="Email Address" className="ml-2 flex-1 border-none bg-transparent text-accent outline-none py-2 px-4" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="mb-4 flex items-center rounded-2xl border border-gray-300 px-4 py-2">
          <img src={ph} alt="phone-icon" />
          <input type="text" placeholder="Phone number" className="ml-2 flex-1 border-none bg-transparent text-accent outline-none py-2 px-4" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} />
        </div>
        <div className="mb-4 flex items-center rounded-2xl border border-gray-300 px-4 py-2 relative">
          <img src={lockIcon} alt="lock-icon" />
          <input 
            type={showPassword ? "text" : "password"} 
            placeholder="Password" 
            className="ml-2 flex-1 border-none bg-transparent text-accent outline-none py-2 px-4" 
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 focus:outline-none">
            <img src={showPassword ? eyeOpen : eyeSlash} alt="toggle password visibility" />
          </button>
        </div>
        <div className="mb-4 flex items-center rounded-2xl border border-gray-300 px-4 py-2 relative">
          <img src={lockIcon} alt="lock-icon" />
          <input 
            type={showConfirmPassword ? "text" : "password"} 
            placeholder="Confirm Password" 
            className="ml-2 flex-1 border-none bg-transparent text-accent outline-none py-2 px-4" 
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-4 focus:outline-none">
            <img src={showConfirmPassword ? eyeOpen : eyeSlash} alt="toggle password visibility" />
          </button>
        </div>
        <div className="flex gap-4">
          <div className="mb-4 flex items-center rounded-2xl border border-gray-300 px-4 py-2">
            <img src={codeIcon} alt="lock-icon" />
            <input 
              type="text" 
              placeholder="Code" 
              className="ml-2 flex-1 border-none bg-transparent text-accent outline-none py-2 px-4" 
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          {codeSent ? (
            <button className="mb-4 rounded-2xl border-2 border-secondary p-2" onClick={handleResendCode} disabled={isSendingCode}>
              {isSendingCode ? "Sending ..." : "Resend Code"}
            </button>
          ) : (
            <button className="mb-4 rounded-2xl border-2 border-secondary p-2" onClick={handleSendCode} disabled={isSendingCode}>
              {isSendingCode ? "Sending ..." : "Send Code"}
            </button>
          )}
        </div>
        <div className="mb-4 flex items-center rounded-2xl border border-gray-300 px-4 py-2">
          <img src={codeIcon} alt="lock-icon" />
          <input
            type="text"
            placeholder="Referral Code (Optional)"
            className="ml-2 flex-1 border-none bg-transparent text-accent outline-none py-2 px-4"
            value={refCode}
            onChange={(e) => setRefCode(e.target.value)}
          />
        </div>
        <button 
          className="w-full rounded-2xl bg-primary text-white py-3 font-bold hover:bg-opacity-90"
          onClick={handleSignup}
          disabled={isSigningUp}
        >
          {isSigningUp ? "Signing Up ..." : "Sign Up"}
        </button>
        <div className="flex justify-between mb-4 mt-4">
          <Link to="/auth/forgot-password" className="text-sm text-secondary hover:underline">Forgot Password?</Link>
          <Link to="/auth/signin" className="text-sm text-secondary hover:underline">Log In</Link>
        </div>
      </form>
    </AuthLayout>
  );
};

export default MobileSignup;
